import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { IoClose } from 'react-icons/io5';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from 'components/Common/Button';
import Select from 'components/Common/Select';
import Input from 'components/Common/Input';
import { BusinessAddon } from '@/apis/business/addons/types';
import { useCancelBusinessAddon } from '@/apis/business/addons/hook';
import { CANCELLATION_REASONS } from '@/constants/cancellationReasons';
import { formatDollars } from '@/utils/helperFunctions';
import { formatCommitmentLabel, getAddonDisplayTitle } from './addonDisplay';

interface DeactivateAddonDrawerProps {
  addon: BusinessAddon | null;
  open: boolean;
  onClose: () => void;
}

type FormValues = {
  cancellation_reason: string;
  cancellation_feedback: string;
};

type CancelMode = 'immediate' | 'end_of_billing_month';

const reasonOptions = CANCELLATION_REASONS.map((reason) => ({
  value: reason.value,
  label: reason.label
}));

function formatDisplayDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

export default function DeactivateAddonDrawer({ addon, open, onClose }: DeactivateAddonDrawerProps) {
  const { mutateAsync: cancelAddon, isPending } = useCancelBusinessAddon();
  const preview = addon?.subscription?.cancellation_preview;
  const allowImmediate = preview?.allow_cancel_immediate !== false;
  const allowEndOfMonth = preview?.allow_cancel_end_of_month !== false;
  const commitmentActive = Boolean(preview?.commitment_active);
  const earlyFee = Number(preview?.early_termination_fee) || 0;
  const cycleEndsLabel = formatDisplayDate(preview?.current_cycle_ends_at);

  const defaultMode: CancelMode = allowEndOfMonth
    ? 'end_of_billing_month'
    : allowImmediate
      ? 'immediate'
      : 'end_of_billing_month';

  const [mode, setMode] = useState<CancelMode>(defaultMode);
  const [agreed, setAgreed] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {
      cancellation_reason: '',
      cancellation_feedback: ''
    }
  });

  const reason = watch('cancellation_reason');
  const feedback = watch('cancellation_feedback');

  useEffect(() => {
    if (!open) return;
    setMode(defaultMode);
    setAgreed(false);
    reset({ cancellation_reason: '', cancellation_feedback: '' });
  }, [open, addon?.id, defaultMode, reset]);

  const title = addon ? `Make ${getAddonDisplayTitle(addon)} Inactive` : 'Make Add-On Inactive';

  const billingNotice = useMemo(() => {
    if (!preview) return null;

    if (mode === 'immediate' && commitmentActive && earlyFee > 0) {
      return `Your commitment is still active (${preview.remaining_months} month${
        preview.remaining_months === 1 ? '' : 's'
      } left). Cancelling now charges an early-termination fee of ${formatDollars(earlyFee)} (remaining months × monthly estimate), then the add-on turns off immediately.`;
    }

    if (mode === 'immediate') {
      return 'The add-on turns off immediately. There is no cancellation fee.';
    }

    if (commitmentActive && earlyFee > 0) {
      return `You keep access until ${cycleEndsLabel}. An early-termination fee of ${formatDollars(earlyFee)} for remaining commitment months is charged when the add-on ends. You can reverse this anytime before then with Keep Add-On Active.`;
    }

    if (commitmentActive) {
      return `You keep access until ${cycleEndsLabel}. You can reverse this anytime before then with Keep Add-On Active.`;
    }

    return `Your minimum commitment is complete. The add-on stays available until ${cycleEndsLabel} — you are not billed again after that and there is no cancellation fee.`;
  }, [preview, mode, commitmentActive, earlyFee, cycleEndsLabel]);

  if (!addon) return null;

  const canSubmit = Boolean(reason) && Boolean(feedback?.trim()) && agreed && (allowImmediate || allowEndOfMonth);

  const onSubmit = async (values: FormValues) => {
    await cancelAddon({
      code: addon.code,
      payload: {
        mode,
        cancellation_reason: values.cancellation_reason,
        cancellation_feedback: values.cancellation_feedback.trim()
      }
    });
    onClose();
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={() => {
        if (!isPending) onClose();
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: 520,
          maxWidth: '100%',
          p: 0,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Stack direction='row' alignItems='center' gap={1.5} minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#FDECEC',
              color: '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <FiAlertTriangle size={20} />
          </Box>
          <Typography variant='h6' fontWeight={600} noWrap>
            {title}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size='small' aria-label='Close' disabled={isPending}>
          <IoClose size={22} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={2.5}>
        <Stack direction='row' flexWrap='wrap' gap={2} mb={2}>
          <Box minWidth={120}>
            <Typography fontSize={12} color='text.secondary'>
              Activated On
            </Typography>
            <Typography fontSize={14} fontWeight={500}>
              {formatDisplayDate(addon.subscription?.activated_at || preview?.activated_at)}
            </Typography>
          </Box>
          <Box minWidth={120}>
            <Typography fontSize={12} color='text.secondary'>
              Commitment
            </Typography>
            <Typography fontSize={14} fontWeight={500}>
              {formatCommitmentLabel(preview?.commitment_months ?? addon.commitment_months)}
            </Typography>
          </Box>
          <Box minWidth={120}>
            <Typography fontSize={12} color='text.secondary'>
              Current Cycle Ends
            </Typography>
            <Typography fontSize={14} fontWeight={500}>
              {cycleEndsLabel}
            </Typography>
          </Box>
        </Stack>

        {billingNotice && (
          <Box
            mb={2.5}
            px={1.5}
            py={1.25}
            borderRadius={1.5}
            bgcolor={mode === 'immediate' && earlyFee > 0 ? '#FEF3C7' : '#F3F4F6'}
          >
            <Typography fontSize={13} color='text.secondary' lineHeight={1.5}>
              {billingNotice}
            </Typography>
            {mode === 'immediate' && earlyFee > 0 && (
              <Typography fontSize={14} fontWeight={600} mt={1} color='text.primary'>
                Due now: {formatDollars(earlyFee)}
              </Typography>
            )}
          </Box>
        )}

        <Typography fontSize={13} fontWeight={600} mb={1}>
          When should this add-on stop?
        </Typography>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as CancelMode)} sx={{ mb: 2.5, gap: 1 }}>
          {allowEndOfMonth && (
            <Box border='1px solid #ECECEC' borderRadius={2} px={1.5} py={1}>
              <FormControlLabel
                value='end_of_billing_month'
                control={<Radio size='small' />}
                sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                label={
                  <Box>
                    <Typography fontSize={14} fontWeight={500}>
                      At the end of this cycle — {cycleEndsLabel}
                    </Typography>
                    <Typography fontSize={12} color='text.secondary'>
                      {commitmentActive && earlyFee > 0
                        ? `Access until that date. Remaining commitment fee of ${formatDollars(earlyFee)} is charged then.`
                        : 'Recommended. Full access until that date, no cancellation fee.'}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
          {allowImmediate && (
            <Box border='1px solid #ECECEC' borderRadius={2} px={1.5} py={1}>
              <FormControlLabel
                value='immediate'
                control={<Radio size='small' />}
                sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                label={
                  <Box>
                    <Typography fontSize={14} fontWeight={500}>
                      Immediately
                    </Typography>
                    <Typography fontSize={12} color='text.secondary'>
                      {commitmentActive && earlyFee > 0
                        ? `Turns off now. Early-termination fee of ${formatDollars(earlyFee)} is charged first.`
                        : 'Turns off now. No cancellation fee.'}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
        </RadioGroup>

        <Stack gap={2} mb={2}>
          <Select
            name='cancellation_reason'
            control={control}
            label='Reason'
            fullWidth
            options={reasonOptions}
            rules={{ required: true }}
          />
          <Input
            name='cancellation_feedback'
            control={control}
            label='Feedback'
            fullWidth
            multiline
            minRows={3}
            placeholder='Tell us more about your experience'
            rules={{ required: true }}
          />
        </Stack>

        <FormControlLabel
          sx={{ alignItems: 'flex-start', ml: 0 }}
          control={
            <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} size='small' sx={{ pt: 0.25 }} />
          }
          label={
            <Typography fontSize={13}>
              {mode === 'immediate'
                ? 'I understand this add-on becomes inactive immediately.'
                : `I understand this add-on becomes inactive on ${cycleEndsLabel}.`}
            </Typography>
          }
        />
      </Box>

      <Stack direction='row' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop='1px solid #ECECEC'>
        <Button variant='outlined' color='inherit' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          disabled={!canSubmit}
          isPending={isPending}
          onClick={handleSubmit(onSubmit)}
          sx={{ textTransform: 'none' }}
        >
          Confirm Cancellation
        </Button>
      </Stack>
    </Drawer>
  );
}
